import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { apiGet, apiPost } from '@/lib/api';
import { formatDateTime } from '@/lib/formatters';
import styles from './NotificationsBell.module.css';

/**
 * The topbar bell: unread conversation count, and a dropdown to actually reach
 * them.
 *
 * The badge alone is not enough. Conversations live on the application detail
 * view, so without this list a user learns they have a message and has no way
 * to find which application it is on — which is exactly how the first cut of
 * this shipped.
 */

// Messages are not time-critical, so this is deliberately unhurried. Polling
// pauses entirely while the tab is hidden rather than hammering a backgrounded
// browser, and resumes on focus.
const UNREAD_POLL_MS = 30000;

// Paralegals use the lawyer dashboard's routes.
const ROLE_BASE = {
  client: '/client',
  lawyer: '/lawyer',
  paralegal: '/lawyer',
  funder: '/funder',
  admin: '/admin',
};

const NotificationsBell = ({ user }) => {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  const unread = threads.filter((t) => t.unread);

  // Closed, the bell only needs a number, so it polls the endpoint that returns
  // one. It used to fetch the whole thread list every 30s purely to count the
  // unread ones — on every authenticated page, for every user, duplicating the
  // inbox's identical poll when that was open.
  const refreshCount = useCallback(async () => {
    if (document.hidden) return;
    try {
      const data = await apiGet('/messages/unread-count');
      setUnreadCount(typeof data?.count === 'number' ? data.count : 0);
    } catch {
      // A failed poll must never surface as an error; the badge keeps its last
      // known value.
    }
  }, []);

  // The list is fetched only when the dropdown actually needs to render it.
  const loadThreads = useCallback(async () => {
    try {
      const all = await apiGet('/messages/threads');
      const list = Array.isArray(all) ? all : [];
      setThreads(list);
      setUnreadCount(list.filter((t) => t.unread).length);
    } catch {
      // Keep whatever is already on screen.
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    refreshCount();
    const timer = setInterval(refreshCount, UNREAD_POLL_MS);
    document.addEventListener('visibilitychange', refreshCount);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshCount);
    };
  }, [user, refreshCount]);

  // Close on outside click and on Escape — a dropdown that traps the page is
  // worse than no dropdown.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadThreads();
  };

  const handleOpenThread = (thread) => {
    setOpen(false);
    const base = ROLE_BASE[user?.role] || '';
    // The fragment is a request, not decoration: the conversation sits below the
    // documents and case detail on an application page, so landing at the top
    // put the thing you clicked a notification for off screen. MessagesPanel
    // reads this and scrolls itself into view.
    navigate(`${base}/application/${thread.application_id}#messages`);
  };

  const handleMarkAllRead = async () => {
    setBusy(true);
    try {
      await Promise.all(unread.map((t) => apiPost(`/messages/threads/${t.thread_id}/read`, {})));
      setThreads((prev) => prev.map((t) => ({ ...t, unread: false })));
      setUnreadCount(0);
    } catch {
      // Leave the badge alone; the next poll reconciles.
    } finally {
      setBusy(false);
    }
  };

  const label = unreadCount > 0
    ? `Notifications, ${unreadCount} unread conversation${unreadCount === 1 ? '' : 's'}`
    : 'Notifications, none unread';

  // Lead each row with whichever party the viewer is NOT.
  //
  // This list spans every application the viewer can see, so the useful label is
  // the one that varies between rows. A client only ever sees their own case —
  // their name on every row says nothing, while the funder is exactly what
  // distinguishes one inquiry from the next. For everyone else the reverse
  // holds: one funder can have inquiries open on many cases, so the case
  // identifies the row and the funder separates rows within it.
  //
  // MessagesPanel, which lists threads inside a single application, always leads
  // with the funder — the same reasoning, since there every thread shares a client.
  const viewerIsClient = user?.role === 'client';

  const rowTitle = (t) => (viewerIsClient
    ? (t.funder_company || t.client_name)
    : (t.client_name || t.funder_company)) || 'Conversation';

  const rowSubtitle = (t) => {
    // A client would only ever see their own name here, so it is omitted.
    if (viewerIsClient) return null;
    return t.client_name && t.funder_company ? t.funder_company : null;
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        className="mcl-topbar-btn"
        onClick={handleToggle}
        title={unreadCount > 0
          ? `${unreadCount} conversation${unreadCount === 1 ? '' : 's'} need your attention`
          : 'No unread messages'}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid="notifications-btn"
        style={{ position: 'relative' }}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className={styles.badge} data-testid="notifications-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className={styles.dropdown} role="menu" data-testid="notifications-dropdown">
          <div className={styles.head}>
            <span>Messages</span>
            {unreadCount > 0 && (
              <button
                className={styles.markAll}
                onClick={handleMarkAllRead}
                disabled={busy}
                data-testid="mark-all-read-btn"
              >
                {busy ? 'Marking…' : 'Mark all read'}
              </button>
            )}
          </div>

          {threads.length === 0 ? (
            <p className={styles.empty} data-testid="notifications-empty">
              No conversations yet.
            </p>
          ) : (
            <ul className={styles.list}>
              {threads.slice(0, 10).map((t) => (
                <li key={t.thread_id}>
                  <button
                    className={styles.item}
                    onClick={() => handleOpenThread(t)}
                    role="menuitem"
                    data-testid={`notification-${t.thread_id}`}
                    data-unread={t.unread ? 'true' : 'false'}
                  >
                    <span className={styles.itemTop}>
                      <span className={styles.itemTitle}>{rowTitle(t)}</span>
                      {rowSubtitle(t) && (
                        <span className={styles.itemSub}>· {rowSubtitle(t)}</span>
                      )}
                      {t.unread && <span className={styles.dot} aria-label="Unread" />}
                    </span>
                    <span className={styles.preview}>
                      {t.last_message_preview || 'No messages yet'}
                    </span>
                    {t.last_message_at && (
                      <span className={styles.when}>{formatDateTime(t.last_message_at)}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
